import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Share2, Copy, Award, TrendingUp } from "lucide-react";
import { toast } from "sonner";

export default function Affiliates() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/affiliates/overview").then(r => setD(r.data)); }, []);
  if (!d) return <div className="p-10 text-zinc-400" data-testid="affiliates-loading">Loading…</div>;

  const Stat = ({ label, value, delta }) => (
    <div className="rounded-2xl bg-white border border-black/5 p-5">
      <p className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className="mt-2 font-display text-2xl font-extrabold">{value}</p>
      {delta && <p className="text-xs text-zinc-500 mt-1">{delta}</p>}
    </div>
  );

  const topAffiliates = d.top_affiliates || [];
  const commissionStructure = d.commission_structure || [];
  const hasActivity = (d.clicks || 0) > 0 || (d.conversions || 0) > 0;

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-6 lg:p-10" data-testid="affiliates-page">
      <div className="flex items-center gap-3 mb-6">
        <Share2 className="h-6 w-6 text-violet-600" />
        <div>
          <h1 className="font-display text-3xl font-bold">Affiliate & Referral</h1>
          <p className="text-sm text-zinc-500 mt-1">Grow revenue by empowering ambassadors</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="affiliate-stats">
        <Stat
          label="Affiliate revenue"
          value={`$${((d.affiliate_revenue || 0) / 1000).toFixed(1)}K`}
          delta={d.active_affiliates ? `${d.active_affiliates} active` : "no activity yet"}
        />
        <Stat
          label="Clicks"
          value={(d.clicks || 0).toLocaleString()}
          delta={hasActivity ? `${(d.conversion_rate || 0).toFixed(2)}% conv` : "—"}
        />
        <Stat
          label="Conversions"
          value={(d.conversions || 0).toLocaleString()}
          delta={d.conversions ? `${d.conversions} sales attributed` : "waiting for first sale"}
        />
        <Stat
          label="Payouts"
          value={`$${((d.payouts || 0) / 1000).toFixed(1)}K`}
          delta={d.pending_payouts ? `Pending $${(d.pending_payouts / 1000).toFixed(1)}K` : "All paid out"}
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 rounded-3xl bg-white border border-black/5 p-6">
          <h3 className="font-display text-lg font-bold mb-4">Top Affiliates</h3>
          {topAffiliates.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-zinc-200 bg-zinc-50/40 p-8 text-center">
              <TrendingUp className="h-7 w-7 text-zinc-300 mx-auto" />
              <p className="mt-2 text-sm font-medium text-zinc-700">No affiliate sales yet</p>
              <p className="text-xs text-zinc-500 mt-1">Share your referral link below — top performers appear here as commissions are earned.</p>
            </div>
          ) : (
            <div className="divide-y divide-black/5" data-testid="affiliate-leaderboard">
              {topAffiliates.map((a, i) => (
                <div key={a.handle || a.name || i} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <span className="h-7 w-7 rounded-full bg-violet-100 text-violet-700 grid place-items-center font-bold text-xs">{i + 1}</span>
                    <div>
                      <p className="text-sm font-medium">{a.name || a.handle || "anonymous"}</p>
                      <p className="text-[11px] text-zinc-500">{a.conversions || 0} conversions</p>
                    </div>
                  </div>
                  <p className="font-display text-lg font-bold text-emerald-600">${(a.revenue || 0).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="rounded-3xl bg-white border border-black/5 p-6">
            <h3 className="font-display text-sm font-bold mb-3">Your Referral Link</h3>
            <div className="flex items-center gap-2">
              <input readOnly value={d.referral_link || ""} className="flex-1 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs font-mono" data-testid="referral-link" />
              <button
                onClick={() => { navigator.clipboard.writeText(d.referral_link || ""); toast.success("Copied"); }}
                className="rounded-xl ashra-gradient-bg px-3 py-2 text-white"
                data-testid="copy-ref"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="rounded-3xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 p-6 text-center" data-testid="affiliate-rank-card">
            <Award className="h-10 w-10 text-amber-500 mx-auto" />
            <p className="mt-2 font-display text-3xl font-extrabold">
              {d.rank ? `#${d.rank}` : "—"}
            </p>
            <p className="text-xs text-amber-700 font-medium">{d.rank_label || "Starting out"}</p>
          </div>
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-black/5 p-6">
        <h3 className="font-display text-lg font-bold mb-4">Commission Structure</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="commission-structure">
          {commissionStructure.map((c, idx) => (
            <div key={c.product_id || c.type || idx} className="rounded-2xl border border-black/5 bg-zinc-50 p-4 text-center">
              <p className="text-xs text-zinc-500 truncate">{c.type || c.tier || "Default"}</p>
              <p className="mt-1 font-display text-2xl font-extrabold text-violet-600">
                {Number(c.pct ?? c.rate ?? 30).toFixed(0)}%
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
